import { defineStore } from 'pinia'
import { ref, computed, shallowRef } from 'vue'
import { getAuthToken } from '@/api/client'
import {
  listConversations,
  getConversation,
  startConversation,
  sendMessage,
  markRead,
  getUnread,
  type SupportConversation,
  type SupportConversationSummary,
  type SupportMessage,
  type StartConversationPayload,
} from '@/api/support'

// Opening Support resumes the latest conversation only while it is fresh or
// has an unread reply; otherwise it starts a new one, which becomes a new
// Slack thread. The customer can always start a new one from the panel.
const RESUME_WINDOW_MS = 2 * 60 * 60 * 1000

const HEARTBEAT_MS = 20000
const PONG_GRACE_MS = 10000

export const useSupportStore = defineStore('support', () => {
  const panelOpen = ref(false)
  const conversations = ref<SupportConversationSummary[]>([])
  // null while composing the first message of a new conversation.
  const active = ref<SupportConversation | null>(null)
  const unread = ref(0)
  const loading = ref(false)
  const showHistory = ref(false)

  const hasUnread = computed(() => unread.value > 0)

  // ---- Live socket ------------------------------------------------------
  // Same liveness rules as the engraver socket: readyState OPEN proves
  // nothing, so ping on an interval and treat a missed pong as dead.
  const ws = shallowRef<WebSocket | null>(null)
  const wantConnection = ref(false)
  let heartbeatTimer: ReturnType<typeof setInterval> | null = null
  let pongTimer: ReturnType<typeof setTimeout> | null = null
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null
  let reconnectAttempt = 0

  function clearTimers() {
    if (heartbeatTimer !== null) { clearInterval(heartbeatTimer); heartbeatTimer = null }
    if (pongTimer !== null) { clearTimeout(pongTimer); pongTimer = null }
  }

  function teardown(socket: WebSocket | null) {
    if (!socket) return
    socket.onopen = socket.onmessage = socket.onclose = socket.onerror = null
    try { socket.close() } catch { /* already closed */ }
  }

  function scheduleReconnect() {
    if (!wantConnection.value || reconnectTimer !== null) return
    // Nobody is waiting on this second to second, so back off further than
    // the engraver socket does.
    const delay = Math.min(1000 * 2 ** reconnectAttempt, 30000)
    reconnectAttempt++
    reconnectTimer = setTimeout(() => { reconnectTimer = null; connect() }, delay)
  }

  function dropAndReconnect() {
    clearTimers()
    const dead = ws.value
    ws.value = null
    teardown(dead)
    scheduleReconnect()
  }

  function ping() {
    const socket = ws.value
    if (!socket || socket.readyState !== WebSocket.OPEN) { dropAndReconnect(); return }
    if (pongTimer !== null) return
    try {
      socket.send(JSON.stringify({ type: 'ping' }))
    } catch {
      dropAndReconnect()
      return
    }
    pongTimer = setTimeout(() => { pongTimer = null; dropAndReconnect() }, PONG_GRACE_MS)
  }

  function connect() {
    wantConnection.value = true
    if (reconnectTimer !== null) { clearTimeout(reconnectTimer); reconnectTimer = null }
    const existing = ws.value
    if (existing && (existing.readyState === WebSocket.OPEN || existing.readyState === WebSocket.CONNECTING)) return
    teardown(existing)
    ws.value = null

    const token = getAuthToken()
    if (!token) { scheduleReconnect(); return }

    const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws'
    const socket = new WebSocket(`${protocol}://${window.location.host}/api/keepsake/support/ws?token=${token}`)
    ws.value = socket

    socket.onopen = () => {
      if (ws.value !== socket) { teardown(socket); return }
      reconnectAttempt = 0
      clearTimers()
      heartbeatTimer = setInterval(ping, HEARTBEAT_MS)
      // Anything sent while we were disconnected was missed; re-read.
      refreshUnread()
      if (active.value) reloadActive()
    }

    socket.onmessage = (event) => {
      if (ws.value !== socket) return
      const data = JSON.parse(event.data)
      if (data.type === 'pong') {
        if (pongTimer !== null) { clearTimeout(pongTimer); pongTimer = null }
        return
      }
      if (data.type === 'message') onIncoming(data.conversation_id, data.message)
      else if (data.type === 'read') refreshUnread()
    }

    socket.onclose = () => {
      if (ws.value !== socket) return
      clearTimers()
      ws.value = null
      scheduleReconnect()
    }

    socket.onerror = () => socket.close()
  }

  function disconnect() {
    wantConnection.value = false
    if (reconnectTimer !== null) { clearTimeout(reconnectTimer); reconnectTimer = null }
    clearTimers()
    teardown(ws.value)
    ws.value = null
  }

  function revalidate() {
    if (!wantConnection.value) return
    const socket = ws.value
    if (!socket || socket.readyState === WebSocket.CLOSED || socket.readyState === WebSocket.CLOSING) {
      dropAndReconnect()
      return
    }
    if (socket.readyState === WebSocket.OPEN) ping()
  }

  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') revalidate()
    })
  }
  if (typeof window !== 'undefined') window.addEventListener('online', revalidate)

  // ---- Messages ---------------------------------------------------------

  function panelIsShowing(conversationId: string) {
    return panelOpen.value
      && !showHistory.value
      && active.value?.id === conversationId
      && (typeof document === 'undefined' || document.visibilityState === 'visible')
  }

  function onIncoming(conversationId: string, message: SupportMessage) {
    if (active.value?.id === conversationId) {
      if (!active.value.messages.some(m => m.id === message.id)) {
        active.value.messages.push(message)
      }
    }
    const summary = conversations.value.find(c => c.id === conversationId)
    if (summary) summary.last_message_at = message.created_at

    if (message.sender !== 'staff') return
    if (panelIsShowing(conversationId)) {
      markRead(conversationId).catch(() => {})
    } else {
      unread.value++
      if (summary) summary.unread++
    }
  }

  async function refreshUnread() {
    try {
      unread.value = (await getUnread()).unread
    } catch { /* keep the last known count */ }
  }

  async function loadConversations() {
    conversations.value = await listConversations()
  }

  async function openConversation(id: string) {
    loading.value = true
    try {
      active.value = await getConversation(id)
      showHistory.value = false
      if (active.value.unread > 0) {
        await markRead(id)
        active.value.unread = 0
        const summary = conversations.value.find(c => c.id === id)
        if (summary) summary.unread = 0
        await refreshUnread()
      }
    } finally {
      loading.value = false
    }
  }

  async function reloadActive() {
    if (!active.value) return
    try {
      const fresh = await getConversation(active.value.id)
      active.value = fresh
      if (fresh.unread > 0 && panelIsShowing(fresh.id)) {
        await markRead(fresh.id)
        active.value.unread = 0
        await refreshUnread()
      }
    } catch { /* leave what is on screen */ }
  }

  /** Open the panel. With an id (from an email link) open that conversation;
   *  otherwise resume the latest one if it is fresh or unread, else start new. */
  async function openPanel(conversationId?: string) {
    panelOpen.value = true
    showHistory.value = false
    try {
      await loadConversations()
      if (conversationId) {
        await openConversation(conversationId)
        return
      }
      const withUnread = conversations.value.find(c => c.unread > 0)
      const latest = conversations.value[0]
      if (withUnread) {
        await openConversation(withUnread.id)
      } else if (latest && Date.now() - new Date(latest.last_message_at).getTime() < RESUME_WINDOW_MS) {
        await openConversation(latest.id)
      } else {
        active.value = null
      }
    } catch {
      active.value = null
    }
  }

  function closePanel() {
    panelOpen.value = false
  }

  function newConversation() {
    active.value = null
    showHistory.value = false
  }

  async function send(text: string, context: Omit<StartConversationPayload, 'message'>) {
    if (!active.value) {
      const conv = await startConversation({ message: text, ...context })
      active.value = conv
      await loadConversations().catch(() => {})
      return
    }
    const msg = await sendMessage(active.value.id, text)
    if (!active.value.messages.some(m => m.id === msg.id)) active.value.messages.push(msg)
  }

  function reset() {
    disconnect()
    panelOpen.value = false
    conversations.value = []
    active.value = null
    unread.value = 0
  }

  return {
    panelOpen, conversations, active, unread, hasUnread, loading, showHistory,
    connect, disconnect, refreshUnread, openPanel, closePanel, openConversation,
    newConversation, send, reset,
  }
})
